// dashboard/components/MySubscriptions.jsx
import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  Avatar,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Telegram,
  CheckCircle,
  Warning,
  Cancel,
  CalendarToday,
  AttachMoney,
  TrendingUp,
  SignalCellularAlt,
  Star,
} from '@mui/icons-material';

const MySubscriptions = () => {
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  const subscriptions = [
    {
      id: 1,
      name: 'VIP Telegram Signals',
      icon: <Telegram sx={{ fontSize: 24, color: '#0088cc' }} />,
      status: 'active',
      daysLeft: 45,
      price: 99.99,
      signalsPerDay: 5,
      winRate: 78,
      isPopular: true,
    },
    {
      id: 2,
      name: 'Premium Trading Course',
      icon: <TrendingUp sx={{ fontSize: 24, color: '#2ecc71' }} />,
      status: 'expiring',
      daysLeft: 12,
      price: 49.99,
      signalsPerDay: 2,
      winRate: 0,
      isPopular: false,
    },
    {
      id: 3,
      name: 'Advanced Trading Alerts',
      icon: <SignalCellularAlt sx={{ fontSize: 24, color: '#9b59b6' }} />,
      status: 'expired',
      daysLeft: 0,
      price: 29.99,
      signalsPerDay: 3,
      winRate: 0,
      isPopular: false,
    },
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return '#2ecc71';
      case 'expiring': return '#f5a623';
      case 'expired': return '#e74c3c';
      default: return '#8a8a9a';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'active': return 'Active';
      case 'expiring': return 'Expiring Soon';
      case 'expired': return 'Expired';
      default: return 'Unknown';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'active': return <CheckCircle sx={{ fontSize: 14 }} />;
      case 'expiring': return <Warning sx={{ fontSize: 14 }} />;
      case 'expired': return <Cancel sx={{ fontSize: 14 }} />;
      default: return <CheckCircle sx={{ fontSize: 14 }} />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
            My Subscriptions
          </Typography>
          <Typography variant="body2" sx={{ color: '#8a8a9a' }}>
            Manage your active subscriptions and plans
          </Typography>
        </Box>
        <Button
          variant="contained"
          sx={{
            bgcolor: '#4a6cf7',
            '&:hover': { bgcolor: '#3a5cd7' },
            borderRadius: 2,
            textTransform: 'none',
          }}
        >
          Upgrade Plan
        </Button>
      </Box>

      <Grid container spacing={3}>
        {subscriptions.map((sub) => (
          <Grid item xs={12} md={4} key={sub.id}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', border: `1px solid ${getStatusColor(sub.status)}30`, position: 'relative' }}>
              {sub.isPopular && (
                <Chip
                  icon={<Star sx={{ fontSize: 14 }} />}
                  label="Popular"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: 16,
                    bgcolor: '#4a6cf7',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '10px',
                    height: 20,
                  }}
                />
              )}
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(0,0,0,0.04)', width: 44, height: 44 }}>
                    {sub.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
                      {sub.name}
                    </Typography>
                    <Chip
                      icon={getStatusIcon(sub.status)}
                      label={getStatusLabel(sub.status)}
                      size="small"
                      sx={{
                        bgcolor: `${getStatusColor(sub.status)}15`,
                        color: getStatusColor(sub.status),
                        fontWeight: 500,
                        height: 20,
                        fontSize: '10px',
                      }}
                    />
                  </Box>
                </Box>

                <Typography variant="h5" sx={{ fontWeight: 700, color: '#4a6cf7', mb: 2 }}>
                  ${sub.price}
                  <Typography component="span" variant="body2" sx={{ color: '#8a8a9a', fontWeight: 400 }}>
                    /mo
                  </Typography>
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#8a8a9a', display: 'block' }}>Days Left</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: sub.daysLeft < 15 ? '#e74c3c' : '#1a1a2e' }}>
                      {sub.daysLeft}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#8a8a9a', display: 'block' }}>Signals/Day</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                      {sub.signalsPerDay}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 1, bgcolor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography variant="caption" sx={{ color: '#8a8a9a', display: 'block' }}>Win Rate</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#2ecc71' }}>
                      {sub.winRate || 'N/A'}%
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      bgcolor: '#1a1a2e',
                      '&:hover': { bgcolor: '#2a2a4e' },
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    {sub.status === 'expired' ? 'Resubscribe' : 'Manage'}
                  </Button>
                  {sub.status === 'active' && (
                    <Button
                      variant="outlined"
                      sx={{
                        borderColor: '#e74c3c',
                        color: '#e74c3c',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': { borderColor: '#c0392b', bgcolor: 'rgba(231, 76, 60, 0.05)' },
                      }}
                      onClick={() => setOpenCancelDialog(true)}
                    >
                      Cancel
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Cancel Dialog */}
      <Dialog open={openCancelDialog} onClose={() => setOpenCancelDialog(false)}>
        <DialogTitle sx={{ color: '#e74c3c', fontWeight: 600 }}>Cancel Subscription</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to cancel your subscription? You will lose access at the end of your billing cycle.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCancelDialog(false)}>Keep</Button>
          <Button onClick={() => setOpenCancelDialog(false)} color="error" variant="contained">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MySubscriptions;